"use client";

import * as React from "react";
import { Mail, Phone, MapPin, Clock, Send, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="31969" className="flex flex-col items-center justify-center h-full min-h-[400px] p-8 text-center bg-secondary/10 rounded-xl border border-secondary/20 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-secondary/20 text-secondary rounded-full flex items-center justify-center mb-6">
          <Send className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-serif font-medium mb-2 text-primary">Analysis Requested</h3>
        <p className="text-muted-foreground mb-8 max-w-md">Thank you for your interest! Our team is preparing your custom rental report and will contact you within 24 hours.</p>
        <Button onClick={resetForm} variant="outline" className="min-w-[150px] border-secondary text-secondary hover:bg-secondary/10">
          Request Another Analysis
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-sans font-medium">Property Owner Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className="focus-visible:ring-secondary border-border" {...field} />
              </FormControl>
              <FormDescription className="sr-only">Enter your full name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-sans font-medium">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="focus-visible:ring-secondary border-border" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-sans font-medium">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(407) 555-0123" className="focus-visible:ring-secondary border-border" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground">Optional – for faster report delivery.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-sans font-medium">Property Address</FormLabel>
              <FormControl>
                <Input placeholder="123 Orlando Ave, Orlando, FL" className="focus-visible:ring-secondary border-border" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-sans font-medium">Additional Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about recent upgrades or unique property features..." className="min-h-[150px] resize-y focus-visible:ring-secondary border-border" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[160px] bg-primary hover:bg-primary-600 text-primary-foreground shadow-md transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-4 w-4" />
            Generating...
          </> : "Get My Free Analysis"}
      </Button>
    </Form>;
}
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="rental-analysis-form" className="bg-[#f5f7f9] relative overflow-hidden ![padding:50px_0px_80px_0px]">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-20">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-primary/10 blur-[120px]" />
        <div className="absolute top-[40%] -right-[10%] w-[40%] h-[40%] rounded-full bg-secondary/15 blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={cn("grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <div className="flex flex-col space-y-8 !justify-start lg:col-span-5">
            <div>
              <span className="inline-block py-1 px-4 rounded-full bg-secondary/10 text-secondary text-xs font-semibold tracking-widest uppercase mb-4">Property Rental analysis</span>
              <h2 className="text-4xl md:text-5xl font-serif text-primary mb-6 leading-tight">
                Maximize Your Orlando Rental Income
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed font-sans">
                Get a data-driven market analysis for your property. Our comprehensive reports help you set the competitive price points and attract high-quality long-term tenants.
              </p>
            </div>

            <div className="space-y-6 py-6">
              <div className="flex items-start space-x-4 group">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-white shadow-sm flex items-center justify-center text-secondary group-hover:bg-secondary group-hover:text-white transition-all duration-300">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-primary mb-1">Local Vacancy Rates</h4>
                  <p className="text-muted-foreground text-sm">Real-time data on occupancy trends in your specific Orlando neighborhood.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-white shadow-sm flex items-center justify-center text-secondary group-hover:bg-secondary group-hover:text-white transition-all duration-300">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-primary mb-1">Comparable Listings</h4>
                  <p className="text-muted-foreground text-sm">Detailed look at similar properties to ensure your pricing is optimized.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-white shadow-sm flex items-center justify-center text-secondary group-hover:bg-secondary group-hover:text-white transition-all duration-300">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-primary mb-1">Suggested Improvements</h4>
                  <p className="text-muted-foreground text-sm">Actionable advice on upgrades that yield the highest return on investment.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-2xl border border-[#e2e8f0] p-6 md:p-8 lg:p-10 h-full">
              <div className="mb-8">
                <h3 className="text-2xl font-serif text-primary mb-2">Request Your Free Rental Analysis</h3>
                <p className="text-muted-foreground font-sans">
                  Complete the details below and our expert analysis team will prepare your custom property report.
                </p>
              </div>

              <FormProvider formKey="rental_analysis_form_rentalanalysis" formId="rental_analysis_form_rentalanalysis" sectionName="contact-section" defaultValues={{
              name: "",
              email: "",
              phone: "",
              subject: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: false
              },
              subject: {
                required: false
              },
              message: {
                required: true
              }
            }} submitText="Get My Free Analysis" successMessage="Thank you for reaching out! We have received your message and will get back to you shortly." errorMessage="We couldn't send your message. Please try again or contact us directly via email." className="w-full">
                <ContactForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}